// src/pages/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../utils/api';

// ✅ Match Login.jsx API_BASE (no trailing spaces)
const API_BASE = (process.env.REACT_APP_API_URL );

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // ✅ Extract token and email from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');
    const urlEmail = params.get('email');
    
    // ✅ Validate required params
    if (!urlToken || !urlEmail) {
      toast.error('Invalid or expired reset link');
      navigate('/forgot-password', { replace: true });
      return;
    }
    
    // ✅ Decode email (handles %40 for @)
    setToken(urlToken);
    setEmail(decodeURIComponent(urlEmail));
  }, [location, navigate]);

  // ✅ Password strength calculator (0-4)
  useEffect(() => {
    const password = formData.password;
    let strength = 0;
    
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    
    setPasswordStrength(Math.min(strength, 4));
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (formData.password.length > 128) {
      newErrors.password = 'Password is too long';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      // ✅ Payload matches backend ResetPasswordRequest model
      const payload = {
        Token: token,
        Email: email,
        NewPassword: formData.password
      };

      console.log('📤 Reset Password Request:', { ...payload, NewPassword: '***', Token: '***' });

      const response = await authenticatedFetch(
        `${API_BASE}/HMO/reset-password`,
        'POST',
        payload
      );

      console.log('✅ Reset Password Response:', response);

      // ✅ Handle success
      if (response?.success === true || response?.status === 'success') {
        setIsSubmitted(true);
        toast.success(response.message || 'Password reset successfully!');
        
        // ✅ Clear sensitive data from localStorage
        localStorage.removeItem('userSession');
        
        // ✅ Redirect to login after delay
        setTimeout(() => {
          navigate('/login', { 
            replace: true,
            state: { 
              message: 'Your password has been reset. Please log in with your new password.'
            }
          });
        }, 3000);
        
      } else {
        // ✅ Handle business logic errors
        const errorMsg = response?.message || 'Failed to reset password. Please try again.';
        
        if (errorMsg.toLowerCase().includes('expired') || errorMsg.toLowerCase().includes('invalid')) {
          setErrors({ submit: 'This reset link has expired or is invalid. Please request a new one.' });
          toast.warning('Reset link expired');
        } else if (errorMsg.toLowerCase().includes('email')) {
          setErrors({ submit: 'Email address does not match reset request.' });
          toast.error('Email mismatch');
        } else {
          setErrors({ submit: errorMsg });
          toast.error(errorMsg);
        }
      }
      
    } catch (error) {
      console.error('💥 Reset Password Error:', { message: error.message, name: error.name });
      
      let msg = 'An error occurred. Please try again.';
      
      if (error.message?.includes('Missing headers') || 
          error.message?.includes('AuthorizationKey') ||
          error.message?.includes('Token') ||
          error.message?.includes('SecretKey')) {
        msg = 'Authentication configuration error. Please contact support.';
      } else if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        msg = 'Cannot connect to server. Check your internet connection.';
      } else if (error.message?.includes('404') || error.message?.includes('Not Found')) {
        msg = 'Reset endpoint not available. Please contact support.';
      } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        msg = 'Invalid or expired reset token. Please request a new password reset.';
      }
      
      setErrors({ submit: msg });
      toast.error(msg);
      
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // ✅ Get password strength label and color
  const getPasswordStrengthInfo = () => {
    const levels = [
      { label: 'Very Weak', color: '#ef4444' },
      { label: 'Weak', color: '#f97316' },
      { label: 'Fair', color: '#eab308' },
      { label: 'Good', color: '#22c55e' },
      { label: 'Strong', color: '#16a34a' }
    ];
    return levels[passwordStrength] || levels[0];
  };

  // ✅ Success State: Show confirmation message
  if (isSubmitted) {
    return (
      <div className="reset-password-page">
        <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

        {/* Header */}
        <header className="header-area header-one">
          <div className="container">
            <div className="d-flex justify-content-between align-items-center py-3">
              <Link to="/" className="brand-logo" aria-label="FSL HMO Home">
                <img src="images/fsl_logo.png" alt="FSL HMO Logo" style={{ height: '50px' }} />
              </Link>
              <button className="theme-btn style-two" onClick={() => navigate('/')} aria-label="Back to Home">← Back to Home</button>
            </div>
          </div>
        </header>

        {/* Success Section */}
        <section className="reset-password-section py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-5 col-md-7">
                <div className="login-container">
                  <div className="text-center mb-4">
                    <div className="success-icon mb-3">
                      <i className="fas fa-check-circle text-success" style={{ fontSize: '48px' }}></i>
                    </div>
                    <h1 className="login-title mb-2">Password Reset Successful!</h1>
                    <p className="login-subtitle">
                      Your password has been updated. Redirecting to login...
                    </p>
                  </div>

                  <div className="alert alert-success mb-4" role="alert">
                    <i className="fas fa-info-circle me-2"></i>
                    <small>You can now log in with your new password.</small>
                  </div>

                  <div className="d-grid">
                    <button 
                      className="btn btn-login"
                      onClick={() => navigate('/login')}
                    >
                      Go to Login <i className="fas fa-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <style>{resetPasswordStyles}</style>
      </div>
    );
  }

  // ✅ Form State: Show reset password form
  return (
    <div className="reset-password-page">
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      {/* Header */}
      <header className="header-area header-one">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <Link to="/" className="brand-logo" aria-label="FSL HMO Home">
              <img src="images/fsl_logo.png" alt="FSL HMO Logo" style={{ height: '50px' }} />
            </Link>
            <button className="theme-btn style-two" onClick={() => navigate('/')} disabled={isLoading} aria-label="Back to Home">← Back to Home</button>
          </div>
        </div>
      </header>

      {/* Reset Password Form */}
      <section className="reset-password-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-5 col-md-7">
              <div className="login-container">
                <div className="login-header text-center mb-4">
                  <h1 className="login-title mb-2">Reset Your Password</h1>
                  <p className="login-subtitle">
                    Enter a new password for <strong>{email}</strong>
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="login-form" noValidate>
                  {/* Global Error Message */}
                  {errors.submit && (
                    <div className="alert alert-danger mb-3" role="alert" aria-live="polite">
                      <i className="fas fa-exclamation-circle me-2" aria-hidden="true"></i>
                      {errors.submit}
                    </div>
                  )}

                  {/* New Password Field */}
                  <div className="form-group mb-3">
                    <label htmlFor="password" className="form-label">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        placeholder="Enter new password (min 8 characters)"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={isLoading}
                        autoComplete="new-password"
                        required
                        aria-invalid={!!errors.password}
                        aria-describedby={errors.password ? 'password-error' : 'password-strength'}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => togglePasswordVisibility('password')}
                        disabled={isLoading}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <i className="fas fa-eye-slash" aria-hidden="true"></i>
                        ) : (
                          <i className="fas fa-eye" aria-hidden="true"></i>
                        )}
                      </button>
                    </div>
                    
                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <div id="password-strength" className="mt-2">
                        <div className="d-flex align-items-center gap-2">
                          <div className="progress flex-grow-1" style={{ height: '6px' }}>
                            <div 
                              className="progress-bar" 
                              role="progressbar"
                              style={{ 
                                width: `${(passwordStrength / 4) * 100}%`,
                                backgroundColor: getPasswordStrengthInfo().color
                              }}
                              aria-valuenow={passwordStrength}
                              aria-valuemin={0}
                              aria-valuemax={4}
                            ></div>
                          </div>
                          <small className="text-muted" style={{ color: getPasswordStrengthInfo().color }}>
                            {getPasswordStrengthInfo().label}
                          </small>
                        </div>
                        <small className="text-muted d-block mt-1">
                          Use 8+ characters with a mix of letters, numbers & symbols
                        </small>
                      </div>
                    )}
                    
                    {errors.password && (
                      <div id="password-error" className="invalid-feedback" role="alert">
                        {errors.password}
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="form-group mb-4">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm New Password <span className="text-danger">*</span>
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                        placeholder="Confirm your new password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={isLoading}
                        autoComplete="new-password"
                        required
                        aria-invalid={!!errors.confirmPassword}
                        aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => togglePasswordVisibility('confirmPassword')}
                        disabled={isLoading}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <i className="fas fa-eye-slash" aria-hidden="true"></i>
                        ) : (
                          <i className="fas fa-eye" aria-hidden="true"></i>
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <div id="confirmPassword-error" className="invalid-feedback" role="alert">
                        {errors.confirmPassword}
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="btn btn-login w-100 mb-3"
                    disabled={isLoading || passwordStrength < 2}
                    aria-busy={isLoading}
                    title={passwordStrength < 2 ? 'Password must be at least "Fair" strength' : undefined}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Resetting...
                      </>
                    ) : (
                      <>
                        Reset Password <i className="fas fa-key ms-2"></i>
                      </>
                    )}
                  </button>

                  {/* Back to Login */}
                  <div className="text-center">
                    <p className="mb-0 text-muted">
                      Remember your password?{' '}
                      <Link to="/login" className="signup-link">
                        Back to Login
                      </Link>
                    </p>
                  </div>

                  {/* Security Notice */}
                  <div className="text-center mt-4 pt-3 border-top">
                    <small className="text-muted">
                      <i className="fas fa-shield-alt me-1" aria-hidden="true"></i>
                      For security, this link expires in 1 hour
                    </small>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{resetPasswordStyles}</style>
    </div>
  );
}

// ✅ Extracted CSS for reusability
const resetPasswordStyles = `
  .reset-password-page { min-height: 100vh; background-color: #fafafa; display: flex; flex-direction: column; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
  .reset-password-section { flex: 1; display: flex; align-items: center; }
  .login-container { background: white; border-radius: 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.08); padding: 40px; width: 100%; animation: slideUp 0.5s ease; border-top: 4px solid #F89423; }
  @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
  .login-header { margin-bottom: 30px; }
  .login-title { font-size: 28px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
  .login-subtitle { color: #6b7280; font-size: 14px; margin: 0; }
  .form-label { font-weight: 600; color: #374151; font-size: 14px; margin-bottom: 8px; display: block; }
  .form-control { padding: 12px 16px; border: 1px solid #d1d5db; border-radius: 10px; font-size: 14px; transition: all 0.3s ease; background-color: #f9fafb; width: 100%; }
  .form-control:focus { border-color: #F89423; box-shadow: 0 0 0 4px rgba(248,148,35,0.15); background-color: white; outline: none; }
  .form-control.is-invalid { border-color: #ef4444; background-color: #fef2f2; }
  .form-control.is-invalid:focus { box-shadow: 0 0 0 4px rgba(239,68,68,0.15); }
  .form-control:disabled { background-color: #f3f4f6; cursor: not-allowed; opacity: 0.8; }
  .invalid-feedback { color: #ef4444; font-size: 12px; margin-top: 4px; display: block; }
  .password-input-wrapper { position: relative; }
  .password-toggle-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #6b7280; cursor: pointer; padding: 8px; transition: color 0.3s ease; display: flex; align-items: center; justify-content: center; }
  .password-toggle-btn:hover:not(:disabled) { color: #F89423; }
  .password-toggle-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .alert { padding: 12px 16px; border-radius: 10px; font-size: 14px; display: flex; align-items: flex-start; }
  .alert-danger { background-color: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
  .alert-danger i { margin-top: 2px; }
  .alert-success { background-color: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
  .alert-success i { margin-top: 2px; }
  .progress { background-color: #e5e7eb; border-radius: 10px; overflow: hidden; }
  .progress-bar { transition: width 0.3s ease, background-color 0.3s ease; }
  .btn-login { background: linear-gradient(135deg, #F89423 0%, #ea7c1f 100%); border: none; color: white; padding: 14px 20px; font-size: 15px; font-weight: 600; border-radius: 10px; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; width: 100%; }
  .btn-login:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(248,148,35,0.4); }
  .btn-login:disabled { opacity: 0.75; cursor: not-allowed; transform: none; }
  .signup-link { color: #F89423; text-decoration: none; font-weight: 600; }
  .signup-link:hover { color: #ea7c1f; text-decoration: underline; }
  .spinner-border { width: 1rem; height: 1rem; border-width: 0.15em; vertical-align: text-bottom; }
  .success-icon { animation: pulse 2s infinite; }
  @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
  @media (max-width: 768px) { .login-container { padding: 30px 20px; border-radius: 12px; } .login-title { font-size: 24px; } }
  @media (max-width: 480px) { .reset-password-section { padding: 20px 0; } .login-container { padding: 25px 15px; } .btn-login { padding: 12px; font-size: 14px; } }
  .form-control:focus-visible, .btn-login:focus-visible, .password-toggle-btn:focus-visible { outline: 2px solid #F89423; outline-offset: 2px; }
`;

export default ResetPassword;